/**
 * useTheme Hook
 *
 * React hook for theme and font size management.
 */

import { useState, useCallback } from 'react';
import {
  Theme,
  themes,
  getCurrentThemeId,
  getTheme,
  setTheme as setThemeService,
  // Font size exports
  FontSizePreset,
  fontSizePresets,
  getCurrentFontSize,
  setFontSize as setFontSizeService,
  increaseFontSize as increaseFontSizeService,
  decreaseFontSize as decreaseFontSizeService,
  resetFontSize as resetFontSizeService,
  fontSizeLimits,
} from '@services/themes';

interface UseThemeResult {
  // Theme
  theme: Theme;
  themes: Theme[];
  setTheme: (themeId: string) => void;
  // Font size
  fontSize: number;
  fontSizePresets: FontSizePreset[];
  fontSizeLimits: { min: number; max: number; default: number };
  setFontSize: (size: number) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
}

/**
 * Hook for managing app theme and font size.
 */
export function useTheme(): UseThemeResult {
  const [currentThemeId, setCurrentThemeId] = useState(() => getCurrentThemeId());
  const [currentFontSize, setCurrentFontSize] = useState(() => getCurrentFontSize());

  const theme = getTheme(currentThemeId);

  const setTheme = useCallback((themeId: string) => {
    setThemeService(themeId);
    setCurrentThemeId(themeId);
  }, []);

  const setFontSize = useCallback((size: number) => {
    setFontSizeService(size);
    setCurrentFontSize(size);
  }, []);

  const increaseFontSize = useCallback(() => {
    const newSize = increaseFontSizeService();
    setCurrentFontSize(newSize);
  }, []);

  const decreaseFontSize = useCallback(() => {
    const newSize = decreaseFontSizeService();
    setCurrentFontSize(newSize);
  }, []);

  const resetFontSize = useCallback(() => {
    const newSize = resetFontSizeService();
    setCurrentFontSize(newSize);
  }, []);

  return {
    // Theme
    theme,
    themes,
    setTheme,
    // Font size
    fontSize: currentFontSize,
    fontSizePresets,
    fontSizeLimits,
    setFontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
  };
}
